import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { type SiteFreshness, opsApi } from "../lib/api";
import { useSortableTable } from "../hooks/useSortableTable";
import { SortHeader } from "../components/SortHeader";

export function OpsPage() {
  const q = useQuery({
    queryKey: ["ops-freshness"],
    queryFn: opsApi.freshness,
    refetchInterval: 60_000,
  });

  const { sortedRows, sortKey, sortDir, toggleSort } = useSortableTable<SiteFreshness>(
    q.data?.sites ?? [],
    {
      site: (s) => s.domain,
      lastAudit: (s) => s.lastAuditAt,
      lastLead: (s) => s.lastLeadAt,
      lastSc: (s) => s.lastScPullAt,
      lastGa4: (s) => s.lastGa4PullAt,
    },
  );

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <h1 className="text-2xl font-display font-bold">Ops</h1>
          <p className="text-ink-600 text-sm mt-1">
            Data-freshness across the portfolio. Stale rows mean cron has
            fallen over — see <code className="bg-ink-100 px-1 rounded text-xs">docs/operator-handbook.md</code> for the recommended
            cron schedule.
            {" · "}
            <Link to="/ops/finance" className="text-accent hover:underline">Finance →</Link>
          </p>
        </div>
        <RefreshDataButton />
      </div>

      {q.isLoading && <div className="bg-white rounded-xl border border-ink-100 p-8 text-center text-ink-500">Loading…</div>}
      {q.error && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-4">
          Failed: {String((q.error as Error).message)}
        </div>
      )}
      {q.data && (
        <>
          <div className="bg-white rounded-xl border border-ink-100 p-5 shadow-sm mb-6">
            <h2 className="font-semibold mb-2">Portfolio-wide</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Tile label="Last journo-query pull" value={q.data.lastJournoPullAt} threshold={2} unit="days" />
              <Tile label="Sites tracked" value={String(q.data.sites.length)} />
            </div>
          </div>

          <div className="bg-white rounded-xl border border-ink-100 overflow-hidden shadow-sm">
            <table className="w-full text-sm">
              <thead className="bg-ink-50 text-xs uppercase tracking-wider text-ink-500">
                <tr>
                  <SortHeader label="Site" sortKey="site" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-left px-4 py-3" />
                  <SortHeader label="Last audit" sortKey="lastAudit" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-left px-4 py-3" />
                  <SortHeader label="Last lead" sortKey="lastLead" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-left px-4 py-3" />
                  <SortHeader label="Last SC pull" sortKey="lastSc" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-left px-4 py-3" />
                  <SortHeader label="Last GA4 pull" sortKey="lastGa4" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-left px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100">
                {sortedRows.map((s) => <Row key={s.id} site={s} />)}
              </tbody>
            </table>
          </div>

          <div className="mt-6 bg-ink-50 border border-ink-100 rounded-lg p-4 text-sm text-ink-600">
            <p className="font-medium mb-1">Recommended cron (paste into <code>crontab -e</code>):</p>
            <pre className="text-xs font-mono bg-white border border-ink-100 p-3 rounded mt-2 overflow-x-auto">
{`# RankForge data fresheners — daily at 6am UTC
0 6 * * * cd /Users/matt/LocalSeoLeads && /opt/homebrew/bin/pnpm rankforge sc-pull --all-sites
30 6 * * * cd /Users/matt/LocalSeoLeads && /opt/homebrew/bin/pnpm rankforge ga4-pull --all-sites
0 7 * * * cd /Users/matt/LocalSeoLeads && /opt/homebrew/bin/pnpm rankforge haro --persist --niches plumbing,roofing,hvac --states TX,FL,NC`}
            </pre>
          </div>
        </>
      )}
    </div>
  );
}

function Row({ site }: { site: SiteFreshness }) {
  return (
    <tr className="hover:bg-ink-50/50">
      <td className="px-4 py-3 font-medium text-ink-900">{site.domain}</td>
      <td className="px-4 py-3"><Cell ts={site.lastAuditAt} thresholdDays={7} /></td>
      <td className="px-4 py-3"><Cell ts={site.lastLeadAt} neverWarn /></td>
      <td className="px-4 py-3"><Cell ts={site.lastScPullAt} thresholdDays={2} /></td>
      <td className="px-4 py-3"><Cell ts={site.lastGa4PullAt} thresholdDays={2} /></td>
    </tr>
  );
}

function Cell({ ts, thresholdDays, neverWarn }: { ts: string | null; thresholdDays?: number; neverWarn?: boolean }) {
  if (!ts) {
    return <span className="text-xs text-ink-400">never</span>;
  }
  const ageMs = Date.now() - new Date(ts).getTime();
  const ageHours = ageMs / 3_600_000;
  const stale = !neverWarn && thresholdDays != null && ageHours > thresholdDays * 24;
  const ageLabel = ageHours < 1 ? "just now"
    : ageHours < 24 ? `${Math.floor(ageHours)}h ago`
    : `${Math.floor(ageHours / 24)}d ago`;
  return (
    <span className={`text-xs ${stale ? "text-red-700 font-bold" : "text-ink-600"}`}>
      {ageLabel}
      {stale && <span className="ml-1">⚠</span>}
    </span>
  );
}

function RefreshDataButton() {
  const qc = useQueryClient();
  const status = useQuery({
    queryKey: ["ops-refresh-status"],
    queryFn: opsApi.refreshDataStatus,
    refetchInterval: 60_000,
  });
  const [flash, setFlash] = useState<{ kind: "ok" | "err"; msg: string } | null>(null);
  const m = useMutation({
    mutationFn: opsApi.refreshData,
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["ops-refresh-status"] });
      qc.invalidateQueries({ queryKey: ["ops-freshness"] });
      if (r.ok) setFlash({ kind: "ok", msg: r.message ?? "Dispatched" });
      else if (r.error === "cooldown") setFlash({ kind: "err", msg: `On cooldown — ${Math.ceil((r.retryAfterSec ?? 0) / 3600)}h left` });
      else setFlash({ kind: "err", msg: r.error ?? "Dispatch failed" });
      setTimeout(() => setFlash(null), 8000);
    },
    onError: (err: Error) => {
      setFlash({ kind: "err", msg: err.message });
      setTimeout(() => setFlash(null), 8000);
    },
  });
  const onCooldown = status.data?.onCooldown ?? false;
  const cooldownLabel = status.data?.retryAfterSec
    ? `${Math.ceil(status.data.retryAfterSec / 3600)}h until next refresh`
    : "";
  const lastLabel = status.data?.lastDispatchedAt
    ? `last manually triggered ${new Date(status.data.lastDispatchedAt).toLocaleString()}`
    : "never manually triggered";
  return (
    <div className="flex flex-col items-end gap-1">
      <button
        onClick={() => m.mutate()}
        disabled={m.isPending || onCooldown}
        className={`px-4 py-2 rounded-md text-sm font-medium ${
          onCooldown
            ? "bg-ink-100 text-ink-400 cursor-not-allowed"
            : "bg-accent text-white hover:bg-accent-600"
        }`}
        title={onCooldown ? cooldownLabel : "Dispatch cron-daily.yml — fresh SC + GA4 + audit + PSI in ~5 min"}
      >
        {m.isPending ? "Dispatching…" : onCooldown ? `↻ Refresh (${cooldownLabel})` : "↻ Refresh portfolio data"}
      </button>
      <span className="text-xs text-ink-500">{lastLabel}</span>
      {flash && (
        <span className={`text-xs ${flash.kind === "ok" ? "text-green-700" : "text-red-700"}`}>
          {flash.msg}
        </span>
      )}
    </div>
  );
}

function Tile({ label, value, threshold, unit }: { label: string; value: string | null; threshold?: number; unit?: string }) {
  let display: string = value ?? "never";
  let stale = false;
  if (value && unit === "days" && threshold) {
    const ageDays = (Date.now() - new Date(value).getTime()) / 86_400_000;
    display = ageDays < 1 ? `${Math.floor(ageDays * 24)}h ago`
      : `${Math.floor(ageDays)}d ago`;
    stale = ageDays > threshold;
  }
  return (
    <div className="bg-ink-50 border border-ink-100 rounded-lg p-3">
      <div className="text-xs uppercase tracking-wider text-ink-500 font-medium">{label}</div>
      <div className={`mt-1 text-lg font-display font-bold ${stale ? "text-red-700" : "text-ink-900"}`}>
        {display}
        {stale && <span className="ml-1">⚠</span>}
      </div>
    </div>
  );
}
